import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import EnrollmentService from "../../_services/EnrollmentService";
import { useEnrollmentSelection } from "./components/useEnrollmentSelection";
import { headerCellStyle, bodyCellStyle } from "./components/SelectableEnrollmentTable";

interface BulkDiscountModalProps {
  show: boolean;
  onHide: () => void;
  selectedEnquiryIds: number[];
  enquiryEnrollmentsMap: Record<number, any[]>;
  studentList: any[];
  onSuccess: () => void;
}

interface Lookup {
  id: number;
  name: string;
}
interface Category {
  value: number;
  label: string;
}

type DiscountRow = {
  discountCategoryId: number; // -1 = Lump Sum
  installmentHeadId: number | "";
  amount: string;
  discountType: number | ""; // 1 = ₹, 2 = %
  remarks: string;
};

const LUMP_SUM = -1;

const cellInput: React.CSSProperties = {
  width: "100%",
  minWidth: 90,
  height: 28,
  padding: "0 6px",
  fontSize: "0.8rem",
  border: "1px solid var(--border-strong)",
  borderRadius: 4,
};

const BulkDiscountModal: React.FC<BulkDiscountModalProps> = ({
  show,
  onHide,
  selectedEnquiryIds,
  enquiryEnrollmentsMap,
  studentList,
  onSuccess,
}) => {
  const { enrollments } = useEnrollmentSelection(
    selectedEnquiryIds,
    enquiryEnrollmentsMap,
    studentList,
    show,
  );

  const [heads, setHeads] = useState<Lookup[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<Record<number, DiscountRow>>({});
  const [submitting, setSubmitting] = useState(false);
  const [summary, setSummary] = useState<any>(null);
  const [error, setError] = useState("");

  const enrollmentKey = useMemo(
    () => enrollments.map((e) => e.enrollmentId).join(","),
    [enrollments],
  );

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setSummary(null);

    // Seed one blank (Lump Sum) row per enrollment.
    const seeded: Record<number, DiscountRow> = {};
    for (const e of enrollments) {
      seeded[e.enrollmentId] = {
        discountCategoryId: LUMP_SUM,
        installmentHeadId: "",
        amount: "",
        discountType: "",
        remarks: "",
      };
    }
    setRows(seeded);

    Promise.all([EnrollmentService.getDiscountHeads(), EnrollmentService.getDiscountCategories()])
      .then(([hResp, cResp]) => {
        if (cancelled) return;
        setHeads(hResp.data?.data?.heads || []);
        setCategories(cResp.data?.data || []);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.response?.data?.message || "Failed to load options");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, enrollmentKey]);

  const setField = (enrollmentId: number, patch: Partial<DiscountRow>) => {
    setRows((prev) => ({ ...prev, [enrollmentId]: { ...prev[enrollmentId], ...patch } }));
  };

  const handleClose = () => {
    setRows({});
    setSummary(null);
    setError("");
    onHide();
  };

  const handleSubmit = async () => {
    setError("");
    const payloadRows = enrollments
      .map((e) => {
        const r = rows[e.enrollmentId];
        if (!r) return null;
        return {
          enrollmentId: e.enrollmentId,
          discountCategoryId: r.discountCategoryId,
          installmentHeadId: r.installmentHeadId ? Number(r.installmentHeadId) : 0,
          amount: Number(r.amount) || 0,
          discountType: r.discountType ? Number(r.discountType) : 0,
          remarks: r.remarks.trim() || null,
        };
      })
      .filter(Boolean) as any[];

    // A row is actionable if it picks a category, or is a complete lump-sum row.
    const actionable = payloadRows.filter(
      (r) =>
        r.discountCategoryId > 0 ||
        (r.installmentHeadId > 0 && r.amount > 0 && (r.discountType === 1 || r.discountType === 2)),
    );
    if (actionable.length === 0) {
      setError("Enter a discount for at least one student (Head + Discount + Type, or a category).");
      return;
    }

    setSubmitting(true);
    try {
      const resp = await EnrollmentService.bulkDiscount({ rows: actionable });
      setSummary(resp.data?.data?.summary || null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to apply bulk discount");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDoneAfterResults = () => {
    handleClose();
    onSuccess();
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>
          {summary ? "Discount — Results" : "Discount"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <div
            style={{
              padding: "8px 12px",
              background: "var(--status-danger-bg)",
              color: "var(--status-danger)",
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {summary ? (
          <div style={{ display: "flex", gap: 12, fontSize: 13, color: "var(--text-strong)" }}>
            <span>
              <strong>{summary.total ?? 0}</strong> rows
            </span>
            <span style={{ color: "var(--status-success-text)" }}>
              <strong>{summary.applied ?? 0}</strong> applied
            </span>
            <span style={{ color: "var(--status-info-text)" }}>
              <strong>{summary.skipped ?? 0}</strong> skipped
            </span>
            <span style={{ color: "var(--status-danger)" }}>
              <strong>{summary.failed ?? 0}</strong> failed
            </span>
          </div>
        ) : loading ? (
          <div style={{ textAlign: "center", padding: 20, color: "var(--text-muted)" }}>
            <i className="fa fa-spinner fa-spin" /> Loading…
          </div>
        ) : (
          <div
            style={{
              overflowX: "auto",
              border: "1px solid var(--border-default)",
              borderRadius: 6,
            }}
          >
            <table style={{ borderCollapse: "collapse", whiteSpace: "nowrap", width: "100%" }}>
              <thead>
                <tr>
                  <th style={headerCellStyle}>Sl no.</th>
                  <th style={headerCellStyle}>Name</th>
                  <th style={headerCellStyle}>Std Code</th>
                  <th style={headerCellStyle}>Discount Type</th>
                  <th style={headerCellStyle}>Head</th>
                  <th style={headerCellStyle}>Discount</th>
                  <th style={headerCellStyle}>Type</th>
                  <th style={headerCellStyle}>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {enrollments.map((e, i) => {
                  const r =
                    rows[e.enrollmentId] || {
                      discountCategoryId: LUMP_SUM,
                      installmentHeadId: "",
                      amount: "",
                      discountType: "",
                      remarks: "",
                    };
                  const isCategory = r.discountCategoryId > 0;
                  return (
                    <tr key={e.enrollmentId}>
                      <td style={bodyCellStyle}>{i + 1}</td>
                      <td style={bodyCellStyle}>{e.studentName}</td>
                      <td style={bodyCellStyle}>{e.studentCode || "—"}</td>
                      <td style={{ ...bodyCellStyle, minWidth: 150 }}>
                        <select
                          value={r.discountCategoryId}
                          onChange={(ev) =>
                            setField(e.enrollmentId, {
                              discountCategoryId: Number(ev.target.value),
                            })
                          }
                          style={cellInput}
                        >
                          <option value={LUMP_SUM}>Lump Sum</option>
                          {categories.map((c) => (
                            <option key={c.value} value={c.value}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ ...bodyCellStyle, minWidth: 150 }}>
                        <select
                          value={r.installmentHeadId}
                          disabled={isCategory}
                          onChange={(ev) =>
                            setField(e.enrollmentId, {
                              installmentHeadId: ev.target.value ? Number(ev.target.value) : "",
                            })
                          }
                          style={cellInput}
                        >
                          <option value="">--Select--</option>
                          {heads.map((h) => (
                            <option key={h.id} value={h.id}>
                              {h.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ ...bodyCellStyle, width: 110 }}>
                        <input
                          type="number"
                          value={r.amount}
                          disabled={isCategory}
                          onChange={(ev) => setField(e.enrollmentId, { amount: ev.target.value })}
                          placeholder="Discount"
                          style={{ ...cellInput, width: 100 }}
                        />
                      </td>
                      <td style={{ ...bodyCellStyle, width: 90 }}>
                        <select
                          value={r.discountType}
                          disabled={isCategory}
                          onChange={(ev) =>
                            setField(e.enrollmentId, {
                              discountType: ev.target.value ? Number(ev.target.value) : "",
                            })
                          }
                          style={{ ...cellInput, width: 80 }}
                        >
                          <option value="">Select</option>
                          <option value={1}>₹</option>
                          <option value={2}>%</option>
                        </select>
                      </td>
                      <td style={{ ...bodyCellStyle, minWidth: 160 }}>
                        <input
                          type="text"
                          value={r.remarks}
                          onChange={(ev) => setField(e.enrollmentId, { remarks: ev.target.value })}
                          placeholder="remarks"
                          style={cellInput}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {summary ? (
          <Button variant="primary" onClick={handleDoneAfterResults}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={handleClose} disabled={submitting}>
              Close
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={submitting || loading || enrollments.length === 0}
            >
              {submitting ? (
                <>
                  <i className="fa fa-spinner fa-spin" /> Submitting…
                </>
              ) : (
                <>Submit</>
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BulkDiscountModal;
